"use client";

import {
  Alert,
  AlertDescription,
} from "@nimble/react-requests/components/ui/alert";
import { useState } from "react";

import { ButtonCountdown } from "@/components/auth/button-countdown";
import { OTP_CONFIG } from "@/components/auth/constants";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import useOTPRequest from "@/hooks/auth/use-otp-request";
import useOTPVerify from "@/hooks/auth/use-otp-verify";
import { useRetryCountdown } from "@/hooks/auth/use-retry-countdown";
import { authTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import { isRetryCountdownActive } from "@/lib/errors/otp-throttle-error";
import { OTPThrottleError } from "@/lib/errors/otp-throttle-error";
import { useAuth } from "@/stores/use-auth";

interface OTPInputContentProps {
  email: string;
  onSuccess: () => void;
  onBack: () => void;
}

export default function OTPInputContent({
  email,
  onSuccess,
  onBack,
}: OTPInputContentProps) {
  const [token, setToken] = useState<string>("");
  const { loadAuth } = useAuth();
  const otpVerify = useOTPVerify();
  const otpRequest = useOTPRequest();
  const retryCountdown = useRetryCountdown(null);

  const handleTokenChange = (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    setToken(numericValue);

    if (numericValue.length === OTP_CONFIG.length) {
      handleVerify(numericValue);
    }
  };

  const handleVerify = async (tokenString: string) => {
    await otpVerify.mutateAsync({ email, token: tokenString });
    onSuccess();
    await loadAuth();
  };

  const handleVerifyClick = () => {
    if (token.length === OTP_CONFIG.length) {
      handleVerify(token);
    }
  };

  const handleResend = () => {
    if (retryCountdown.isActive) return;

    setToken("");
    otpVerify.reset();
    otpRequest.mutate(email, {
      onError: (error: Error) => {
        if (error instanceof OTPThrottleError) {
          retryCountdown.startCountdown(error.retryAfterSeconds);
        } else {
          retryCountdown.clearCountdown();
        }
      },
      onSuccess: () => {
        retryCountdown.clearCountdown();
      },
    });
  };

  const errorMessage =
    otpVerify.error?.message ?? otpRequest.error?.message ?? null;
  const isRetryActive = isRetryCountdownActive(retryCountdown.secondsRemaining);

  return (
    <div className="space-y-3 sm:space-y-4">
      {errorMessage && (
        <Alert className="border-destructive/20 bg-destructive-soft">
          <AlertDescription className="text-destructive text-sm">
            {errorMessage}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-center">
        <InputOTP
          value={token}
          onChange={handleTokenChange}
          maxLength={OTP_CONFIG.length}
          containerClassName="has-[:disabled]:opacity-30"
          disabled={otpVerify.isPending}
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          data-testid={authTestIds.OTP_INPUT}
        >
          <InputOTPGroup className="gap-1.5 sm:gap-2">
            {Array.from({ length: OTP_CONFIG.length }).map((_, index) => (
              <InputOTPSlot
                key={index}
                index={index}
                className="w-10 h-11 sm:w-11 sm:h-12 text-center text-base sm:text-lg font-semibold border-border focus:border-secondary focus:ring-2 focus:ring-secondary/20 rounded-lg"
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      <Button
        type="button"
        onClick={handleVerifyClick}
        disabled={token.length < OTP_CONFIG.length || otpVerify.isPending}
        className="w-full h-10 sm:h-11 text-sm font-semibold bg-secondary hover:bg-secondary/90 text-primary-foreground transition-all shadow-sm hover:shadow-md rounded-lg"
        data-testid={authTestIds.OTP_VERIFY_BUTTON}
      >
        {otpVerify.isPending ? "Verifying..." : "Verify Code"}
      </Button>

      <div className="text-center space-y-0.5">
        <p className="text-[11px] sm:text-xs text-ink-subtle">
          Didn&apos;t receive it?{" "}
          <button
            type="button"
            onClick={handleResend}
            disabled={
              otpVerify.isPending || otpRequest.isPending || isRetryActive
            }
            className="text-secondary hover:underline font-medium disabled:opacity-50"
            data-testid={authTestIds.OTP_RESEND_BUTTON}
          >
            <ButtonCountdown
              isLoading={otpRequest.isPending}
              isRetryActive={isRetryActive}
            >
              <ButtonCountdown.Loading>sending...</ButtonCountdown.Loading>
              <ButtonCountdown.Retry>Retry</ButtonCountdown.Retry>
              <ButtonCountdown.Default>Resend code</ButtonCountdown.Default>
            </ButtonCountdown>
          </button>
        </p>
        {isRetryActive && (
          <p className="text-[10px] sm:text-xs text-ink-subtle">
            You have requested too many codes. Please wait{" "}
            {retryCountdown.secondsRemaining} second
            {retryCountdown.secondsRemaining !== 1 ? "s" : ""} before trying
            again.
          </p>
        )}
        <p className="text-[10px] sm:text-xs text-ink-faint">
          Expires in {OTP_CONFIG.expirationTimeMinutes} min
        </p>
      </div>

      <Button
        variant="ghost"
        onClick={onBack}
        disabled={otpVerify.isPending}
        className="w-full text-xs sm:text-sm text-ink-subtle h-7 sm:h-8"
        data-testid={authTestIds.OTP_BACK_BUTTON}
      >
        ← Back
      </Button>
    </div>
  );
}
